import { contentTypeFor } from "@sagot/constants";
import { AVATAR_MIME_TYPES, WIDGET_LIMITS } from "./widget-model";

export type ImageProblem = "type" | "empty" | "size";
export type IconSizeProblem = "unreadable" | "small" | "large";

export interface ImageSize {
  width: number;
  height: number;
}

type PickedFile = Pick<File, "type" | "size"> & { name?: string };

const BYTES_PER_MB = 1024 * 1024;
const MB_DECIMALS = 10;

/** Why a picked file can't be a widget brand image (PNG/JPEG/WebP, 1 byte to 2 MB); `null` = fine. */
export function imageProblem(file: PickedFile): ImageProblem | null {
  // Judged by extension first: some OSes report no type for `.webp`.
  const type = contentTypeFor({ name: file.name ?? "", type: file.type });
  if (!AVATAR_MIME_TYPES.some((accepted) => accepted === type)) return "type";
  if (file.size <= 0) return "empty";
  return file.size > WIDGET_LIMITS.avatarMaxBytes ? "size" : null;
}

/** File size in MB, rounded up to one decimal ("2.1 MB" for a file just over 2 MB, never a "2 MB" that looks within the limit). */
export function megabytes(bytes: number): string {
  return String(Math.ceil((bytes / BYTES_PER_MB) * MB_DECIMALS) / MB_DECIMALS);
}

/** Why an image of this size can't be the launcher icon (`null` size = the browser couldn't read it). */
export function iconSizeProblem(size: ImageSize | null): IconSizeProblem | null {
  if (!size || size.width <= 0 || size.height <= 0) return "unreadable";
  if (Math.min(size.width, size.height) < WIDGET_LIMITS.launcherIconMinPx) return "small";
  return Math.max(size.width, size.height) > WIDGET_LIMITS.launcherIconMaxPx ? "large" : null;
}

/**
 * The picture's natural size from its header, `null` when the browser can't decode it (damaged
 * file, or HTML/SVG renamed to `.png`). Nothing is painted, so a huge canvas costs no memory here.
 */
export function readImageSize(file: Blob): Promise<ImageSize | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    const finish = (size: ImageSize | null) => {
      URL.revokeObjectURL(url);
      resolve(size);
    };
    image.onload = () => {
      finish({ width: image.naturalWidth, height: image.naturalHeight });
    };
    image.onerror = () => {
      finish(null);
    };
    image.src = url;
  });
}
